import { useCallback, useState } from 'react'

import {
  Badge,
  Card,
  Code,
  Collapse,
  Group,
  NumberFormatter,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import dayjs from 'dayjs'

import type {
  AdminRedemptionDetail as AdminRedemptionDetailType,
  AdminRedemptionStatus,
  RedemptionAllocation,
  RedemptionConversion,
  RedemptionDeprovision,
  RedemptionDisbursement,
  RedemptionProvision,
} from '@repo/common/queries/admin/redeem'

import { BlockchainAddress } from '@/components/ui/BlockchainAddress'
import { BlockchainTransactionCard } from '@/components/ui/BlockchainTransactionCard'
import { TowerCard } from '@/components/ui/TowerCard'

import styles from './RedemptionDetail.module.css'

interface Props {
  redemption: AdminRedemptionDetailType | undefined
  isLoading: boolean
  redeemableId: string
}

const REVERSAL_STATUSES = new Set<AdminRedemptionStatus>([
  'reversing',
  'reversed',
  'deprovisioning',
  'unconverting',
])

type MainPhaseKey =
  | 'awaiting'
  | 'processing'
  | 'provisioning'
  | 'converting'
  | 'settling'
  | 'disbursing'
  | 'completed'

type ReversalPhaseKey =
  | 'reversing'
  | 'unconverting'
  | 'deprovisioning'
  | 'reversed'

type MetadataPhaseKey =
  | 'recovery_required'
  | 'webhook_enqueued'
  | 'callback_sent'
  | 'expires'

type TimelinePhaseKey = MainPhaseKey | ReversalPhaseKey | MetadataPhaseKey

const MAIN_PHASE_ORDER: MainPhaseKey[] = [
  'awaiting',
  'processing',
  'provisioning',
  'converting',
  'settling',
  'disbursing',
  'completed',
]

const REVERSAL_PHASE_ORDER: ReversalPhaseKey[] = [
  'reversing',
  'unconverting',
  'deprovisioning',
  'reversed',
]

const STATUS_TO_MAIN_PHASE: Record<AdminRedemptionStatus, MainPhaseKey> = {
  awaiting: 'awaiting',
  receiving: 'awaiting',
  processing: 'processing',
  provisioning: 'provisioning',
  converting: 'converting',
  settling: 'settling',
  disbursing: 'disbursing',
  completed: 'completed',
  failed: 'completed',
  reversing: 'completed',
  reversed: 'completed',
  deprovisioning: 'completed',
  unconverting: 'completed',
}

const STATUS_TO_REVERSAL_PHASE: Partial<
  Record<AdminRedemptionStatus, ReversalPhaseKey>
> = {
  reversing: 'reversing',
  unconverting: 'unconverting',
  deprovisioning: 'deprovisioning',
  reversed: 'reversed',
}

const PHASE_LABELS: Record<TimelinePhaseKey, string> = {
  awaiting: 'Awaiting',
  processing: 'Processing',
  provisioning: 'Provisioning',
  converting: 'Converting',
  settling: 'Settling',
  disbursing: 'Disbursing',
  completed: 'Completed',
  reversing: 'Reversing',
  unconverting: 'Unconverting',
  deprovisioning: 'Deprovisioning',
  reversed: 'Reversed',
  recovery_required: 'Recovery Required',
  webhook_enqueued: 'Webhook Enqueued',
  callback_sent: 'Callback Sent',
  expires: 'Expires',
}

type PhaseStatus = 'completed' | 'current' | 'future' | 'failed' | 'reversal'

interface TimelinePhase {
  key: TimelinePhaseKey
  label: string
  timestamp: string | null
  status: PhaseStatus
  expandable: boolean
}

function statusColor(status: AdminRedemptionStatus): string {
  if (status === 'completed') return 'green'
  if (status === 'failed') return 'red'
  if (REVERSAL_STATUSES.has(status)) return 'orange'
  if (status === 'awaiting') return 'gray'
  return 'blue'
}

const Field: React.FC<{
  label: string
  children: React.ReactNode
  span?: number
}> = ({ label, children, span }) => (
  <Stack gap={2} style={span ? { gridColumn: `span ${span}` } : undefined}>
    <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
      {label}
    </Text>
    <Text size="sm" fw={500}>
      {children}
    </Text>
  </Stack>
)

function minCreatedAt(nodes: { created_at: string }[]): string | null {
  if (nodes.length === 0) return null
  const first = nodes[0]!
  return nodes.reduce(
    (min, n) => (n.created_at < min ? n.created_at : min),
    first.created_at,
  )
}

const NumericValue: React.FC<{ value: number | null | undefined }> = ({
  value,
}) => {
  if (value == null) return <Text size="sm">—</Text>
  return <NumberFormatter value={value} thousandSeparator />
}

const ProvisionCard: React.FC<{ provision: RedemptionProvision }> = ({
  provision,
}) => (
  <Card withBorder radius="sm" p="sm">
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          {provision.liquidity_provider.name}
        </Text>
        <Code style={{ fontSize: '0.7rem' }}>{provision.id.slice(0, 8)}</Code>
      </Group>
      <SimpleGrid cols={3} spacing="xs">
        <Field label="Total">
          <NumericValue value={provision.total} />
        </Field>
        <Field label="Commission">
          <NumericValue value={provision.commission_fees} />
        </Field>
        <Field label="LP Fees">
          <NumericValue value={provision.lp_fees} />
        </Field>
        <Field label="Operating Fees">
          <NumericValue value={provision.operating_fees} />
        </Field>
        <Field label="Asset">{provision.asset_id}</Field>
        <Field label="Denomination">{provision.denomination}</Field>
      </SimpleGrid>
      <BlockchainTransactionCard
        transaction={provision.blockchain_transaction}
        label="Transaction"
      />
    </Stack>
  </Card>
)

const ConversionCard: React.FC<{ conversion: RedemptionConversion }> = ({
  conversion,
}) => (
  <Card withBorder radius="sm" p="sm">
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          {conversion.source_asset_id} → {conversion.destination_asset_id}
        </Text>
        <Group gap="xs">
          <Badge
            size="xs"
            variant="light"
            color={conversion.status === 'completed' ? 'green' : 'blue'}
          >
            {conversion.status}
          </Badge>
          {conversion.unconversion && (
            <Badge size="xs" variant="light" color="orange">
              Unconversion
            </Badge>
          )}
        </Group>
      </Group>
      <SimpleGrid cols={3} spacing="xs">
        <Field label="Total">
          <NumericValue value={conversion.total} />
        </Field>
        <Field label="Fees">
          <NumericValue value={conversion.conversion_fees} />
        </Field>
        <Field label="Type">{conversion.conversion_type}</Field>
      </SimpleGrid>
      <BlockchainTransactionCard
        transaction={conversion.burn_transaction}
        label="Burn"
      />
      <BlockchainTransactionCard
        transaction={conversion.mint_transaction}
        label="Mint"
      />
    </Stack>
  </Card>
)

const DeprovisionCard: React.FC<{
  deprovision: RedemptionDeprovision
  allocation?: RedemptionAllocation
}> = ({ deprovision, allocation }) => (
  <Card withBorder radius="sm" p="sm">
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          {deprovision.liquidity_provider.name}
        </Text>
        <Badge
          size="xs"
          variant="light"
          color={deprovision.status === 'completed' ? 'green' : 'blue'}
        >
          {deprovision.status}
        </Badge>
      </Group>
      <SimpleGrid cols={3} spacing="xs">
        <Field label="Total">
          <NumericValue value={deprovision.total} />
        </Field>
        <Field label="Return Total">
          <NumericValue value={deprovision.return_total} />
        </Field>
        <Field label="Denomination">{deprovision.denomination}</Field>
      </SimpleGrid>
      <BlockchainTransactionCard
        transaction={deprovision.return_transaction}
        label="Return Transaction"
      />
      {allocation && (
        <>
          <Text size="xs" c="dimmed" tt="uppercase" fw={500} mt="xs">
            Recovery Allocation
          </Text>
          <SimpleGrid cols={3} spacing="xs">
            <Field label="Total">
              <NumericValue value={allocation.total} />
            </Field>
            <Field label="Balance After">
              <NumericValue value={allocation.lpb_balance_after} />
            </Field>
            <Field label="Denomination">{allocation.denomination}</Field>
          </SimpleGrid>
          <BlockchainTransactionCard
            transaction={allocation.blockchain_transaction}
            label="Allocation Transaction"
          />
        </>
      )}
    </Stack>
  </Card>
)

const DisbursementCard: React.FC<{ disbursement: RedemptionDisbursement }> = ({
  disbursement,
}) => (
  <Card withBorder radius="sm" p="sm">
    <Stack gap="xs">
      <Group justify="space-between">
        <Badge size="xs" variant="light" color="blue">
          {disbursement.disbursement_type}
        </Badge>
        <Badge
          size="xs"
          variant="light"
          color={disbursement.status === 'completed' ? 'green' : 'blue'}
        >
          {disbursement.status}
        </Badge>
      </Group>
      <SimpleGrid cols={3} spacing="xs">
        <Field label="Total">
          <NumericValue value={disbursement.total} />
        </Field>
        <Field label="Amount">
          <NumericValue value={disbursement.amount} />
        </Field>
        <Field label="Asset">{disbursement.asset_id}</Field>
      </SimpleGrid>
      <BlockchainTransactionCard
        transaction={disbursement.blockchain_transaction}
        label="Transaction"
      />
    </Stack>
  </Card>
)

function getPhaseTimestamp(
  key: MainPhaseKey,
  r: AdminRedemptionDetailType,
): string | null {
  switch (key) {
    case 'awaiting':
      return r.created_at
    case 'processing':
      return r.processing_at
    case 'provisioning':
      return minCreatedAt(r.redemption_provisions.nodes)
    case 'converting':
      return minCreatedAt(
        r.redemption_conversions.nodes.filter((c) => !c.unconversion),
      )
    case 'settling':
      return r.settled_at
    case 'disbursing':
      return minCreatedAt(r.redemption_disbursements.nodes)
    case 'completed':
      return r.finalized_at
  }
}

function getReversalPhaseTimestamp(
  key: ReversalPhaseKey,
  r: AdminRedemptionDetailType,
): string | null {
  switch (key) {
    case 'reversing':
      return null
    case 'unconverting':
      return minCreatedAt(
        r.redemption_conversions.nodes.filter((c) => c.unconversion),
      )
    case 'deprovisioning':
      return minCreatedAt(r.redemption_deprovisions.nodes)
    case 'reversed':
      return r.status === 'reversed' ? r.finalized_at : null
  }
}

function hasPhaseData(
  key: MainPhaseKey | ReversalPhaseKey,
  r: AdminRedemptionDetailType,
): boolean {
  switch (key) {
    case 'processing':
      return !!(r.deposit_total || r.deposit_amount || r.deposit_transaction)
    case 'provisioning':
      return r.redemption_provisions.nodes.length > 0
    case 'converting':
      return (
        r.redemption_conversions.nodes.filter((c) => !c.unconversion).length > 0
      )
    case 'settling':
      return !!(
        r.settlement_total ||
        r.settlement_amount ||
        r.settlement_transaction
      )
    case 'disbursing':
      return r.redemption_disbursements.nodes.length > 0
    case 'reversing':
      return !!r.reversal_transaction
    case 'unconverting':
      return (
        r.redemption_conversions.nodes.filter((c) => c.unconversion).length > 0
      )
    case 'deprovisioning':
      return r.redemption_deprovisions.nodes.length > 0
    case 'reversed':
      return !!r.fee_recovery_transaction
    default:
      return false
  }
}

function findFailurePointIndex(r: AdminRedemptionDetailType): number {
  for (let i = 0; i < MAIN_PHASE_ORDER.length; i++) {
    const phase = MAIN_PHASE_ORDER[i]!
    const ts = getPhaseTimestamp(phase, r)
    if (ts == null && phase !== 'awaiting') {
      if (phase === 'provisioning' && r.redemption_provisions.nodes.length > 0)
        continue
      if (
        phase === 'converting' &&
        r.redemption_conversions.nodes.filter((c) => !c.unconversion).length > 0
      )
        continue
      if (phase === 'disbursing' && r.redemption_disbursements.nodes.length > 0)
        continue
      return i
    }
  }
  return MAIN_PHASE_ORDER.length
}

function buildTimeline(r: AdminRedemptionDetailType): TimelinePhase[] {
  const phases: TimelinePhase[] = []
  const status = r.status
  const isReversal = REVERSAL_STATUSES.has(status)
  const isFailed = status === 'failed'
  const isCompleted = status === 'completed'

  const currentMainPhase = STATUS_TO_MAIN_PHASE[status]
  const currentMainIndex = MAIN_PHASE_ORDER.indexOf(currentMainPhase)

  let failureIndex = MAIN_PHASE_ORDER.length
  if (isFailed || isReversal) {
    failureIndex = findFailurePointIndex(r)
  }

  for (let i = 0; i < MAIN_PHASE_ORDER.length; i++) {
    const key = MAIN_PHASE_ORDER[i]!

    if (isReversal && i >= failureIndex) break

    const ts = getPhaseTimestamp(key, r)

    let phaseStatus: PhaseStatus
    if (isCompleted) {
      phaseStatus = 'completed'
    } else if (isFailed || isReversal) {
      if (i < failureIndex) {
        phaseStatus = ts != null ? 'completed' : 'future'
      } else if (i === failureIndex) {
        phaseStatus = 'failed'
      } else {
        phaseStatus = 'future'
      }
    } else {
      if (i < currentMainIndex) {
        phaseStatus = 'completed'
      } else if (i === currentMainIndex) {
        phaseStatus = 'current'
      } else {
        phaseStatus = 'future'
      }
    }

    phases.push({
      key,
      label: PHASE_LABELS[key],
      timestamp: ts,
      status: phaseStatus,
      expandable: hasPhaseData(key, r),
    })
  }

  if (isReversal) {
    const currentReversalPhase = STATUS_TO_REVERSAL_PHASE[status]
    const currentReversalIndex = currentReversalPhase
      ? REVERSAL_PHASE_ORDER.indexOf(currentReversalPhase)
      : -1

    for (let i = 0; i < REVERSAL_PHASE_ORDER.length; i++) {
      const key = REVERSAL_PHASE_ORDER[i]!
      const ts = getReversalPhaseTimestamp(key, r)

      const phaseStatus: PhaseStatus =
        i <= currentReversalIndex ? 'reversal' : 'future'

      phases.push({
        key,
        label: PHASE_LABELS[key],
        timestamp: ts,
        status: phaseStatus,
        expandable: hasPhaseData(key, r),
      })
    }
  }

  if (r.recovery_required_at) {
    phases.push({
      key: 'recovery_required',
      label: PHASE_LABELS.recovery_required,
      timestamp: r.recovery_required_at,
      status: 'completed',
      expandable: false,
    })
  }

  if (r.webhook_enqueued_at) {
    phases.push({
      key: 'webhook_enqueued',
      label: PHASE_LABELS.webhook_enqueued,
      timestamp: r.webhook_enqueued_at,
      status: 'completed',
      expandable: false,
    })
  }

  if (r.successful_callbacked_at) {
    phases.push({
      key: 'callback_sent',
      label: PHASE_LABELS.callback_sent,
      timestamp: r.successful_callbacked_at,
      status: 'completed',
      expandable: false,
    })
  }

  phases.push({
    key: 'expires',
    label: PHASE_LABELS.expires,
    timestamp: r.expires_at,
    status: 'future',
    expandable: false,
  })

  return phases
}

function dotClassName(status: PhaseStatus): string {
  switch (status) {
    case 'completed':
      return styles.dot_completed
    case 'current':
      return styles.dot_current
    case 'future':
      return styles.dot_future
    case 'failed':
      return styles.dot_failed
    case 'reversal':
      return styles.dot_reversal
  }
}

function formatTimestamp(value: string | null, isExpires: boolean): string {
  if (isExpires && value == null) return 'Never'
  if (value == null) return '—'
  return dayjs(value).format('MMM D, YYYY h:mm:ss A')
}

function renderPhaseContent(
  key: TimelinePhaseKey,
  r: AdminRedemptionDetailType,
): React.ReactNode {
  switch (key) {
    case 'processing':
      return (
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg">
            <Field label="Deposit Total">
              <NumericValue value={r.deposit_total} />
            </Field>
            <Field label="Deposit Amount (raw)">
              {r.deposit_amount ?? '—'}
            </Field>
            <Field label="Disbursement Status">
              {r.deposit_disbursement_status ?? '—'}
            </Field>
            <Field label="Transaction Hash">
              {r.deposit_transaction_hash ?? '—'}
            </Field>
          </SimpleGrid>
          <BlockchainTransactionCard
            transaction={r.deposit_transaction}
            label="Deposit Transaction"
          />
        </Stack>
      )
    case 'provisioning':
      return (
        <Stack gap="sm">
          {r.redemption_provisions.nodes.map((p) => (
            <ProvisionCard key={p.id} provision={p} />
          ))}
        </Stack>
      )
    case 'converting':
      return (
        <Stack gap="sm">
          {r.redemption_conversions.nodes
            .filter((c) => !c.unconversion)
            .map((c) => (
              <ConversionCard key={c.id} conversion={c} />
            ))}
        </Stack>
      )
    case 'settling':
      return (
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg">
            <Field label="Settlement Total">
              <NumericValue value={r.settlement_total} />
            </Field>
            <Field label="Settlement Amount (raw)">
              {r.settlement_amount ?? '—'}
            </Field>
          </SimpleGrid>
          <Stack gap="xs">
            <BlockchainTransactionCard
              transaction={r.settlement_transaction}
              label="Settlement"
            />
            <BlockchainTransactionCard
              transaction={r.onchain_approval_transaction}
              label="Onchain Approval"
            />
            <BlockchainTransactionCard
              transaction={r.onchain_pull_transaction}
              label="Onchain Pull"
            />
            <BlockchainTransactionCard
              transaction={r.onchain_pull_close_transaction}
              label="Onchain Pull Close"
            />
          </Stack>
        </Stack>
      )
    case 'disbursing':
      return (
        <Stack gap="sm">
          {r.redemption_disbursements.nodes.map((d) => (
            <DisbursementCard key={d.id} disbursement={d} />
          ))}
        </Stack>
      )
    case 'reversing':
      return (
        <BlockchainTransactionCard
          transaction={r.reversal_transaction}
          label="Reversal Transaction"
        />
      )
    case 'unconverting':
      return (
        <Stack gap="sm">
          {r.redemption_conversions.nodes
            .filter((c) => c.unconversion)
            .map((c) => (
              <ConversionCard key={c.id} conversion={c} />
            ))}
        </Stack>
      )
    case 'deprovisioning': {
      const allocsByIdMap = new Map(
        r.redemption_allocations.nodes.map((a) => [a.id, a]),
      )
      return (
        <Stack gap="sm">
          {r.redemption_deprovisions.nodes.map((d) => (
            <DeprovisionCard
              key={d.id}
              deprovision={d}
              allocation={
                d.return_allocation_id
                  ? allocsByIdMap.get(d.return_allocation_id)
                  : undefined
              }
            />
          ))}
        </Stack>
      )
    }
    case 'reversed':
      return (
        <BlockchainTransactionCard
          transaction={r.fee_recovery_transaction}
          label="Fee Recovery Transaction"
        />
      )
    default:
      return null
  }
}

const TimelineItem: React.FC<{
  phase: TimelinePhase
  expanded: boolean
  onToggle: () => void
  redemption: AdminRedemptionDetailType
  hideConnector?: boolean
}> = ({ phase, expanded, onToggle, redemption, hideConnector }) => {
  const content = phase.expandable
    ? renderPhaseContent(phase.key, redemption)
    : null

  return (
    <div
      className={`${styles.timeline_phase} ${hideConnector ? styles.timeline_phase_hide_connector : ''}`}
    >
      <div className={styles.timeline_indicator}>
        <div
          className={`${styles.timeline_dot} ${dotClassName(phase.status)}`}
        />
      </div>
      <div className={styles.timeline_content}>
        <div
          className={`${styles.phase_header} ${phase.expandable ? styles.phase_header_expandable : ''}`}
          onClick={phase.expandable ? onToggle : undefined}
        >
          <span className={styles.phase_label}>{phase.label}</span>
          <span className={styles.phase_timestamp}>
            {formatTimestamp(phase.timestamp, phase.key === 'expires')}
          </span>
          {phase.expandable && (
            <span className={styles.phase_chevron}>
              {expanded ? (
                <IconChevronDown size={14} />
              ) : (
                <IconChevronRight size={14} />
              )}
            </span>
          )}
        </div>
        {phase.expandable && (
          <Collapse in={expanded}>
            <div className={styles.phase_expanded_content}>{content}</div>
          </Collapse>
        )}
      </div>
    </div>
  )
}

export const RedemptionDetail: React.FC<Props> = ({
  redemption,
  isLoading,
  redeemableId,
}) => {
  const [expandedPhases, setExpandedPhases] = useState<Set<TimelinePhaseKey>>(
    new Set(),
  )

  const togglePhase = useCallback((key: TimelinePhaseKey) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  if (isLoading || !redemption) {
    return (
      <Stack gap="md">
        <TowerCard title="Redemption">
          <Stack gap="md">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={20} width="70%" />
            ))}
          </Stack>
        </TowerCard>
      </Stack>
    )
  }

  const timeline = buildTimeline(redemption)

  return (
    <Stack gap="md">
      <Group>
        <Link
          to="/redeem/$id"
          params={{ id: redeemableId }}
          className={styles.back_link}
        >
          <Group gap={4}>
            <IconArrowLeft size={14} />
            <Text size="sm">Back to Redeemable</Text>
          </Group>
        </Link>
      </Group>

      {/* Header */}
      <TowerCard
        title={
          <Group gap="xs">
            <Text fw={700} size="lg">
              Redemption
            </Text>
            <Code style={{ fontSize: '0.8rem' }}>{redemption.id}</Code>
          </Group>
        }
        extraHeader={
          <Badge
            size="lg"
            variant="light"
            color={statusColor(redemption.status)}
          >
            {redemption.status}
          </Badge>
        }
      >
        <Stack gap="md">
          {redemption.error && (
            <Badge variant="light" color="red" size="sm">
              Error: {redemption.error}
            </Badge>
          )}
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="lg">
            <Field label="Deposit Total">
              <NumericValue value={redemption.deposit_total} />
            </Field>
            <Field label="Settlement Total">
              <NumericValue value={redemption.settlement_total} />
            </Field>
            <Field label="Fees Total">
              <NumericValue value={redemption.fees_total} />
            </Field>
            <Field label="Commission Total">
              <NumericValue value={redemption.commission_fee_total} />
            </Field>
            <Field label="Commission %">
              {redemption.commission_fee_percent != null
                ? `${redemption.commission_fee_percent}%`
                : '—'}
            </Field>
            <Field label="Commission Expenses">
              <NumericValue value={redemption.commission_fee_expenses} />
            </Field>
            <Field label="Commission LP">
              <NumericValue value={redemption.commission_fee_lp} />
            </Field>
            <Field label="Commission Operating">
              <NumericValue value={redemption.commission_fee_operating} />
            </Field>
            <Field label="Token Price">
              <NumericValue value={redemption.token_price} />
            </Field>
            <Field label="Total AUM">
              <NumericValue value={redemption.total_aum} />
            </Field>
            <Field label="Post-Redemption AUM %">
              {redemption.post_redemption_aum_percent != null
                ? `${redemption.post_redemption_aum_percent}%`
                : '—'}
            </Field>
            <Field label="Retry Count">{redemption.retry_count}</Field>
          </SimpleGrid>
        </Stack>
      </TowerCard>

      {/* Timeline */}
      <TowerCard title="Timeline">
        <div>
          {timeline.map((phase, i) => (
            <TimelineItem
              key={phase.key}
              phase={phase}
              expanded={expandedPhases.has(phase.key)}
              onToggle={() => {
                togglePhase(phase.key)
              }}
              redemption={redemption}
              hideConnector={
                redemption.status === 'completed' &&
                timeline[i + 1]?.key === 'expires'
              }
            />
          ))}
        </div>
      </TowerCard>

      {/* Network & Addresses */}
      <TowerCard title="Network & Addresses">
        <SimpleGrid cols={{ base: 2, md: 3 }} spacing="lg">
          <Field label="Network">
            <Code style={{ fontSize: '0.75rem' }}>{redemption.network}</Code>
          </Field>
          <Field label="Source Address">
            <BlockchainAddress
              address={redemption.source_address}
              network={redemption.network}
            />
          </Field>
          <Field label="Deposit Address">
            <BlockchainAddress
              address={redemption.deposit_address}
              network={redemption.network}
            />
          </Field>
          {redemption.source_address && (
            <Field label="Redeemer Address">
              <BlockchainAddress
                address={redemption.source_address}
                network={redemption.network}
              />
            </Field>
          )}
          <Field label="Deposit Token">
            {redemption.redeemable_network.deposit_token.symbol} (
            {redemption.redeemable_network.deposit_token.network})
          </Field>
          <Field label="Settlement Token">
            {redemption.redeemable_network.settlement_token.symbol} (
            {redemption.redeemable_network.settlement_token.network})
          </Field>
        </SimpleGrid>
      </TowerCard>
    </Stack>
  )
}
